import * as PIXI from "pixi.js";
import { symbolData, isCoinSymbol } from "../../../SymbolData";
import { ASSET_NAME } from "../../../assets";
import { FONT_CONFIG } from "../../../textureConfigs";
import { formatToCustomLocale } from "../../../utilities";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import store from "../../../State/Store/store";
import { openSymbolPopup, closeSymbolPopup } from "../../../State/actions";
import { PixiSettings } from "../../../pixiSettings";

// Configuration constants for info popup positioning, sizing, and styling
const INFO_SYMBOL_POPUP_CONFIG = {
  // Font sizes
  PAYTABLE_FONT_SIZE: 25, // Reduced from 28 to prevent overflow
  DESCRIPTION_FONT_SIZE: 25, // Reduced from 28 to prevent overflow
  LINE_HEIGHT_MULTIPLIER: 1.3,
  CONTENT_PADDING: 0.15,

  // Base dimensions before texture scaling
  POPUP_WIDTH_MULTIPLIER: 2.2,
  POPUP_HEIGHT_MULTIPLIER: 1.15,

  // Frame scaling (applied to popup dimensions)
  FRAME_WIDTH_SCALE: 1.25,
  FRAME_HEIGHT_SCALE: 1.45,

  // Positioning
  SYMBOL_Y_OFFSET_MULTIPLIER: -1.5, // Vertical offset for clickable areas and popup (symbolHeight * this)

  // Coin value text (BitmapText)
  COIN_VALUE_FONT_SIZE_MULTIPLIER: 0.25, // fontSize = symbolHeight * this
  COIN_VALUE_MAX_WIDTH_MULTIPLIER: 0.9, // maxWidth = symbolHeight * this

  // Text container sizing
  TEXT_CONTAINER_WIDTH_MULTIPLIER: 0.9, // Increased to allow more width for descriptions
  TEXT_START_X_OFFSET_MULTIPLIER_LEFT: 0.46, // X offset multiplier for left side popups
  TEXT_START_X_OFFSET_MULTIPLIER_RIGHT: 0.4, // X offset multiplier for right side popups

  // Text spacing
  TEXT_GAP_MULTIPLIER: 0.3, // gap = fontSize * this

  // Texture offsets (for left/right side positioning)
  POPUP_TEXTURE_X_OFFSET_LEFT: -0.08,
  POPUP_TEXTURE_Y_OFFSET_LEFT: -0.03,
  POPUP_TEXTURE_X_OFFSET_RIGHT: 0.08,
  POPUP_TEXTURE_Y_OFFSET_RIGHT: -0.03,
};

/**
 * InfoOverlayController - Manages symbol info popups dynamically
 * Creates popup sprites on-demand when symbols are clicked
 * Only one popup can be active at a time
 */
class InfoOverlayController {
  constructor(
    overlayContainer,
    dimensions,
    reelRefs,
    t, // i18n translation function
    bet, // Current bet value
    overlayReelRefs // Overlay reel refs for checking expanding wild state
  ) {
    this.overlayContainer = overlayContainer;
    this.dimensions = dimensions;
    this.reelRefs = reelRefs;
    this.overlayReelRefs = overlayReelRefs;
    this.t = t;
    this.bet = bet || 1; // Store bet value
    this.baseBet = 1; // Base bet for paytable calculations (will be updated via updateBaseBet())

    // Active popup state
    this.activePopup = null;
    this.activeSymbolPosition = null; // "reelIndex-symbolIndex"

    // Clickable areas (always present, lightweight)
    this.clickableAreas = [];

    // Cached textures
    this.infoFrameTexture = PIXI.Assets.get(ASSET_NAME.info_frame);

    // Track device type for desktop-specific behavior
    this.device = dimensions.device;

    // Create background click area for closing popup
    this._createBackgroundClickArea();

    // Create 20 clickable areas (5 reels × 4 symbols)
    this._createClickableAreas();
  }

  /**
   * Creates a full-screen background click area for closing popup
   */
  _createBackgroundClickArea() {
    const { width, height } = this.dimensions;

    this.backgroundClickArea = new PIXI.Sprite(PIXI.Texture.EMPTY);
    this.backgroundClickArea.width = width * 3;
    this.backgroundClickArea.height = height * 3;
    this.backgroundClickArea.anchor.set(0.5);
    this.backgroundClickArea.x = 0;
    this.backgroundClickArea.y = 0;
    this.backgroundClickArea.alpha = 0;
    this.backgroundClickArea.eventMode = "none"; // Start disabled
    this.backgroundClickArea.zIndex = 15; // Between clickable areas (10) and popup (20)
    this.backgroundClickArea.name = "background_click_area";

    this.backgroundClickArea.on("pointerdown", () => {
      this.closePopup();
    });

    this.overlayContainer.addChild(this.backgroundClickArea);
  }

  /**
   * Enable/disable background click area
   */
  setBackgroundClickAreaActive(active) {
    if (this.backgroundClickArea) {
      this.backgroundClickArea.eventMode = active ? "static" : "none";
    }
  }

  /**
   * Creates invisible clickable sprites over each symbol position
   * Creates 20 clickable areas (5 reels × 4 symbols) for popup interaction
   */
  _createClickableAreas() {
    const { symbolWidth, symbolHeight, reelSpacing } = this.dimensions;

    for (let reelIndex = 0; reelIndex < 5; reelIndex++) {
      for (let symbolIndex = 0; symbolIndex < 4; symbolIndex++) {
        const clickArea = new PIXI.Sprite(PIXI.Texture.EMPTY);
        clickArea.width = symbolWidth;
        clickArea.height = symbolHeight;
        clickArea.anchor.set(0.5);
        clickArea.alpha = 0.01; // Nearly invisible but still interactive
        clickArea.eventMode = "static";
        clickArea.cursor = "pointer";
        clickArea.zIndex = 10; // Above overlay background

        // Position based on reel grid (matches InfoReel positioning)
        clickArea.x = Math.round(reelSpacing * (reelIndex - 2));
        clickArea.y = Math.round(
          symbolHeight * symbolIndex +
          symbolHeight * INFO_SYMBOL_POPUP_CONFIG.SYMBOL_Y_OFFSET_MULTIPLIER
        );

        // Store indices for click handler
        clickArea.reelIndex = reelIndex;
        clickArea.symbolIndex = symbolIndex;

        clickArea.on("pointerdown", (event) => {
          this.handleSymbolClick(reelIndex, symbolIndex);
          event.stopPropagation();
        });

        this.clickableAreas.push(clickArea);
        this.overlayContainer.addChild(clickArea);
      }
    }
  }

  /**
   * Handles symbol click
   * Desktop: Always opens popup (can switch between symbols)
   * Mobile/Tablet: If popup is open, close it. Otherwise open it.
   */
  handleSymbolClick(reelIndex, symbolIndex) {
    soundManager.play(SOUND_NAME.button_click);
    
    // On mobile/tablet: if any popup is open, just close it
    if (!this.isDesktop() && this.activePopup) {
      this.closePopup();
      return;
    }
    
    // Desktop or no popup open: open the popup
    this.openPopup(reelIndex, symbolIndex);
  }

  /**
   * Opens popup - creates all sprites dynamically on-demand
   * Creates frame sprite, symbol texture, coin value text (if coin), and paytable/description text
   */
  openPopup(reelIndex, symbolIndex) {
    // Close existing popup first
    this.closePopup();

    const positionId = `${reelIndex}-${symbolIndex}`;
    this.activeSymbolPosition = positionId;

    // Get symbol data from reel controller
    const reelSymbolData = this._getSymbolDataFromReel(reelIndex, symbolIndex);

    if (!reelSymbolData.texture || !reelSymbolData.symbolId) {
      return; // Can't create popup without data
    }

    // Create popup container
    const popupContainer = new PIXI.Container();
    popupContainer.name = "active_info_popup";
    popupContainer.sortableChildren = true;
    popupContainer.zIndex = 20; // Above clickable areas

    // Position popup container at symbol position
    const { symbolWidth, symbolHeight, reelSpacing } = this.dimensions;
    popupContainer.x = Math.round(reelSpacing * (reelIndex - 2));
    popupContainer.y = Math.round(
      symbolHeight * symbolIndex +
      symbolHeight * INFO_SYMBOL_POPUP_CONFIG.SYMBOL_Y_OFFSET_MULTIPLIER
    );

    // Calculate dimensions
    const popupWidth =
      symbolWidth * INFO_SYMBOL_POPUP_CONFIG.POPUP_WIDTH_MULTIPLIER;
    const popupHeight =
      symbolHeight * INFO_SYMBOL_POPUP_CONFIG.POPUP_HEIGHT_MULTIPLIER;
    const isLeftSide = reelIndex < 3;

    // Set up container for desktop hover detection with smaller hitArea
    if (this.isDesktop()) {
      const hitboxScale = 0.7; // 70% of full area
      const fullWidth = popupWidth + symbolWidth;
      const fullHeight = popupHeight + symbolHeight;
      const hitWidth = fullWidth * hitboxScale;
      const hitHeight = fullHeight * hitboxScale;

      popupContainer.eventMode = "static";
      popupContainer.hitArea = new PIXI.Rectangle(
        -hitWidth / 2,
        -hitHeight / 2,
        hitWidth,
        hitHeight
      );

      popupContainer.on("pointerleave", () => {
        this.closePopup();
      });
    } else {
      popupContainer.eventMode = "none";
    }

    // Create frame sprite
    const frameSprite = this._createFrameSprite(
      popupWidth,
      popupHeight,
      isLeftSide
    );
    popupContainer.addChild(frameSprite);

    // Create symbol texture copy
    const symbolSprite = new PIXI.Sprite(reelSymbolData.texture);
    symbolSprite.anchor.set(0.5);
    symbolSprite.scale.set(reelSymbolData.scale);
    symbolSprite.x = 0;
    symbolSprite.y = 0;
    symbolSprite.zIndex = 20;
    symbolSprite.name = "info_symbol_texture";
    popupContainer.addChild(symbolSprite);

    // Create coin value text if it's a coin symbol
    // Use the actual value from the reel (frozen) instead of recalculating with current bet
    // Matches ReelControllerV2.js scaling logic exactly for visual consistency
    const symbolInfo = symbolData[reelSymbolData.symbolId];
    let coinValueText = null;
    if (symbolInfo?.value && reelSymbolData.coinValueText) {
      // Use the frozen coin value from the reel
      const displayValue = reelSymbolData.coinValueText;
      if (displayValue) {
        const { symbolHeight, symbolWidth } = this.dimensions;
        const fontSize = Math.round(
          symbolHeight *
          INFO_SYMBOL_POPUP_CONFIG.COIN_VALUE_FONT_SIZE_MULTIPLIER
        );
        coinValueText = new PIXI.BitmapText(displayValue, {
          fontName: "font",
          fontSize: fontSize,
        });
        coinValueText.anchor.set(0.5);
        coinValueText.name = "coin_value";
        coinValueText.zIndex = 21; // Above symbol sprite
        coinValueText.x = 0;
        coinValueText.y = 0;

        // Apply ReelControllerV2.js scaling logic (matches spinning reels exactly)
        // Reset scale first
        coinValueText.scale.set(1);

        // Force update to get accurate measurements
        coinValueText.updateText();

        // Calculate available width (with padding - matches ReelControllerV2.js)
        const coinWidth = symbolWidth;
        const paddingPercent = 0.12; // 12% padding on each side (same as ReelControllerV2.js)
        const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

        const actualTextWidth = coinValueText.width;

        // Only apply scaling if text is too wide
        if (actualTextWidth > maxTextWidth) {
          // Count digits (excluding formatting characters like commas and dots)
          const digitCount = displayValue.replace(/[^0-9]/g, '').length;

          // Apply scaling method based on digit count (matches ReelControllerV2.js)
          if (digitCount <= 4) {
            // 1-4 digits: Horizontal squishing only
            const scaleX = maxTextWidth / actualTextWidth;
            coinValueText.scale.x = scaleX;
          } else if (digitCount >= 5 && digitCount <= 8) {
            // 5-8 digits: Horizontal squishing only (preserve height)
            const scaleX = maxTextWidth / actualTextWidth;
            coinValueText.scale.x = scaleX;
          } else {
            // 9+ digits: Uniform scaling (both width and height)
            const scale = maxTextWidth / actualTextWidth;
            coinValueText.scale.set(scale);
          }
        }

        popupContainer.addChild(coinValueText);
      }
    }

    // Create text content
    const textElements = this._createTextContent(
      reelSymbolData.symbolId,
      isLeftSide,
      popupWidth,
      popupHeight
    );
    textElements.forEach((el) => popupContainer.addChild(el));

    // Store reference for cleanup and bet updates
    this.activePopup = {
      container: popupContainer,
      frameSprite,
      symbolSprite,
      coinValueText,
      textElements,
      reelIndex,
      symbolIndex,
      symbolId: reelSymbolData.symbolId,
    };

    this.overlayContainer.addChild(popupContainer);

    // Dispatch Redux action
    store.dispatch(openSymbolPopup(positionId));
  }

  /**
   * Closes popup - destroys all dynamically created sprites
   * Cleans up frame, symbol texture, coin value text, and paytable text elements
   */
  closePopup() {
    if (!this.activePopup) {
      return;
    }

    // Remove from overlay
    this.overlayContainer.removeChild(this.activePopup.container);

    // Destroy all sprites
    this.activePopup.frameSprite.destroy({ children: true });
    this.activePopup.symbolSprite.destroy();
    if (this.activePopup.coinValueText) {
      this.activePopup.coinValueText.destroy();
    }
    this.activePopup.textElements.forEach((el) => el.destroy());
    this.activePopup.container.destroy({ children: true });

    this.activePopup = null;
    this.activeSymbolPosition = null;

    // Dispatch Redux action
    store.dispatch(closeSymbolPopup());
  }

  /**
   * Creates the decorative frame sprite for the popup
   * Positions frame on left or right side based on reel position
   */
  _createFrameSprite(popupWidth, popupHeight, isLeftSide) {
    const { symbolWidth, symbolHeight, dividerDimensions } = this.dimensions;

    const frameSprite = new PIXI.Sprite(this.infoFrameTexture);
    frameSprite.anchor.set(0.5);
    frameSprite.width = popupWidth * INFO_SYMBOL_POPUP_CONFIG.FRAME_WIDTH_SCALE;
    frameSprite.height =
      popupHeight * INFO_SYMBOL_POPUP_CONFIG.FRAME_HEIGHT_SCALE;
    frameSprite.name = "info_popup_visual";

    // Calculate frame position (matches InfoSymbolPopup logic)
    let popupX;
    if (isLeftSide) {
      popupX = -symbolWidth / 2 - dividerDimensions.width;
    } else {
      popupX = symbolWidth / 2 - popupWidth + dividerDimensions.width;
    }

    frameSprite.x = Math.round(
      popupX +
      popupWidth / 2 +
      symbolWidth *
        (isLeftSide
          ? INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_X_OFFSET_LEFT
          : INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_X_OFFSET_RIGHT)
    );

    frameSprite.y = Math.round(
      -symbolHeight / 2 -
      dividerDimensions.width +
      popupHeight / 2 +
      symbolHeight *
        (isLeftSide
          ? INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_Y_OFFSET_LEFT
          : INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_Y_OFFSET_RIGHT)
    );

    return frameSprite;
  }

  /**
   * Gets symbol texture, scale, and ID from Redux state and rendered sprite
   * If expanding wild is active on this reel, returns wild symbol instead
   * Uses same data source logic as Reel.jsx: currentRound.reels after first spin, initialReels otherwise
   * Retrieves actual texture and scale from ReelControllerV2's rendered sprite
   */
  _getSymbolDataFromReel(reelIndex, symbolIndex) {
    // Check if expanding wild is active on this reel
    const overlayReel = this.overlayReelRefs?.current?.[reelIndex];
    const overlayController = overlayReel?.getOverlayReelController?.();

    // If expanding wild is visible, return wild symbol (ID 1)
    if (overlayController?.isExpandingWildVisible) {
      const WILD_SYMBOL_ID = 1;
      const symbolInfo = symbolData[WILD_SYMBOL_ID];

      if (!symbolInfo?.texture) {
        return { texture: null, scale: 1, symbolId: WILD_SYMBOL_ID };
      }

      // Get wild texture directly from symbolData
      const textureName = symbolInfo.texture;
      const reel = this.reelRefs.current[reelIndex];
      const controller = reel?.getReelController();

      if (!controller?.spritesheet?.textures) {
        return { texture: null, scale: 1, symbolId: WILD_SYMBOL_ID };
      }

      // Get the wild texture from spritesheet
      const wildTexture = controller.spritesheet.textures[`${textureName}.png`];

      if (!wildTexture) {
        return { texture: null, scale: 1, symbolId: WILD_SYMBOL_ID };
      }

      // Get scale from any visible sprite
      const VISIBLE_SYMBOL_START_INDEX = 5;
      const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
      const spriteData = controller.spinningSprites?.[spriteIndex];
      const scale = spriteData?.normalSprite?.scale?.x || 1;

      return {
        texture: wildTexture,
        scale: scale,
        symbolId: WILD_SYMBOL_ID,
      };
    }

    // Original logic - get actual symbol underneath
    const state = store.getState();
    const currentRound = state.game?.currentRound;
    const initialReels = state.game?.initialReels;

    // Use currentRound.reels if available (after first spin), otherwise use initialReels
    let reelData;
    if (currentRound?.reels && currentRound.reels[reelIndex]) {
      reelData = currentRound.reels[reelIndex];
    } else if (initialReels && initialReels[reelIndex]) {
      reelData = initialReels[reelIndex];
    } else {
      return { texture: null, scale: 1, symbolId: null };
    }

    const symbolId = reelData[symbolIndex];
    if (symbolId === undefined || symbolId === null) {
      return { texture: null, scale: 1, symbolId: null };
    }

    // Get symbol info from SymbolData
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      return { texture: null, scale: 1, symbolId: null };
    }

    // Check if symbol has texture (blank symbols don't)
    const textureName = symbolInfo.texture;
    if (!textureName || textureName === null) {
      return { texture: null, scale: 1, symbolId };
    }

    // Access the actual rendered sprite from controller to get texture and scale
    const reel = this.reelRefs.current[reelIndex];
    const controller = reel?.getReelController();

    if (!controller?.spinningSprites) {
      return { texture: null, scale: 1, symbolId };
    }

    // Visible sprites start at index 5 in ReelControllerV2
    const VISIBLE_SYMBOL_START_INDEX = 5;
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    const spriteData = controller.spinningSprites[spriteIndex];

    if (!spriteData?.normalSprite) {
      return { texture: null, scale: 1, symbolId };
    }

    return {
      texture: spriteData.normalSprite.texture,
      scale: spriteData.normalSprite.scale?.x || 1,
      symbolId: symbolId,
      coinValueText: spriteData.valueText?.text || null, // Get the actual coin value from the reel
    };
  }

  /**
   * Creates text content based on symbol type
   * Returns description text for wild/scatter/coin, or paytable entries for regular symbols
   */
  _createTextContent(symbolId, isLeftSide, popupWidth, popupHeight) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) return [];

    const state = store.getState();
    const paytable = state.game.paytable;
    const bet = state.game.bet;
    const currency = state.game.currency;
    const { symbolScale } = this.dimensions;

    // Determine symbol type
    const symbolType = this._getSymbolType(symbolId, symbolInfo);

    // Calculate text container position
    const textContainerWidth =
      this.dimensions.symbolWidth *
      INFO_SYMBOL_POPUP_CONFIG.TEXT_CONTAINER_WIDTH_MULTIPLIER;
    const textContainerX = isLeftSide
      ? this.dimensions.symbolWidth
      : -this.dimensions.symbolWidth;

    if (symbolType === "wild") {
      return this._createDescriptionText(
        this.t("wild_minipaytable"),
        textContainerX,
        textContainerWidth,
        symbolScale
      );
    }

    if (symbolType === "scatter") {
      return this._createDescriptionText(
        this.t("PAYTABLE.info_popup_scatter_description"),
        textContainerX,
        textContainerWidth,
        symbolScale
      );
    }

    if (symbolType === "coin") {
      return this._createDescriptionText(
        this.t("coin_minipaytable"),
        textContainerX,
        textContainerWidth,
        symbolScale
      );
    }

    // Regular symbol - create paytable
    return this._createPaytableText(
      symbolId,
      paytable,
      bet,
      currency,
      textContainerX,
      textContainerWidth,
      symbolScale,
      isLeftSide
    );
  }

  /**
   * Determines symbol type from symbolPay field
   * Returns: "wild", "scatter", "coin" (symbolPay starts with "c"), or "regular"
   */
  _getSymbolType(symbolId, symbolInfo) {
    if (!symbolInfo || !symbolInfo.symbolPay) return "regular";

    const symbolPay = symbolInfo.symbolPay;

    // Check symbol type based on symbolPay field
    if (symbolPay === "wild") return "wild";
    if (symbolPay === "scatter") return "scatter";

    // Coin symbols have symbolPay starting with "c" (c1, c2, c3, etc.)
    if (symbolPay.startsWith("c")) return "coin";

    // Everything else is regular (hp, mp, lp)
    return "regular";
  }

  /**
   * Creates description text for wild/scatter/coin symbols
   * Returns single centered PIXI.Text sprite
   */
  _createDescriptionText(
    text,
    textContainerX,
    textContainerWidth,
    symbolScale
  ) {
    const descriptionFontSize = Math.round(
      INFO_SYMBOL_POPUP_CONFIG.DESCRIPTION_FONT_SIZE * symbolScale
    );

    const textSprite = new PIXI.Text(text, {
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: descriptionFontSize,
      fontWeight: "bold",
      fill: 0xffffff,
      align: "center",
      wordWrap: true,
      wordWrapWidth: textContainerWidth,
    });

    textSprite.anchor.set(0.5);
    textSprite.x = Math.round(textContainerX);
    textSprite.y = 0;
    textSprite.resolution = 2;
    textSprite.name = "info_popup_description";

    return [textSprite];
  }

  /**
   * Creates paytable text entries for regular symbols
   * Returns array of PIXI.Text sprites showing symbol count and bet-multiplied payout
   * Format: "5: 1.00 FUN" (symbol count : formatted payout currency)
   */
  _createPaytableText(
    symbolId,
    paytable,
    bet,
    currency,
    textContainerX,
    textContainerWidth,
    symbolScale,
    isLeftSide
  ) {
    if (!paytable || !paytable[symbolId]) return [];

    // Build display entries (same logic as InfoSymbolPopup)
    const displayEntries = paytable[symbolId]
      .map((value, idx) => ({ value, originalIndex: idx }))
      .reverse()
      .filter((item) => item.value !== 0)
      .map((item, displayIndex) => {
        const symbolCount = paytable[symbolId].length - displayIndex;
        const payoutAmount = item.value * (bet / this.baseBet);
        const formattedPayout = formatToCustomLocale(payoutAmount);
        return {
          symbolCount,
          formattedPayout,
          symbolColor: 0xffff00,
          payoutColor: 0xffffff,
        };
      });

    if (displayEntries.length === 0) return [];

    const fontSize = Math.round(
      INFO_SYMBOL_POPUP_CONFIG.PAYTABLE_FONT_SIZE * symbolScale
    );
    const lineHeight =
      fontSize * INFO_SYMBOL_POPUP_CONFIG.LINE_HEIGHT_MULTIPLIER;

    // Calculate scale factor to fit text
    const paytableScaleFactor = this._calculatePaytableScaleFactor(
      displayEntries,
      fontSize,
      textContainerWidth,
      currency
    );

    const adjustedFontSize = Math.round(fontSize * paytableScaleFactor);
    const adjustedGap =
      adjustedFontSize * INFO_SYMBOL_POPUP_CONFIG.TEXT_GAP_MULTIPLIER;

    // Calculate vertical positioning
    const totalContentHeight = displayEntries.length * lineHeight;
    const startY = -totalContentHeight / 2;
    // Use different offset multiplier based on orientation
    const offsetMultiplier = isLeftSide
      ? INFO_SYMBOL_POPUP_CONFIG.TEXT_START_X_OFFSET_MULTIPLIER_LEFT
      : INFO_SYMBOL_POPUP_CONFIG.TEXT_START_X_OFFSET_MULTIPLIER_RIGHT;
    const textStartX = textContainerX - textContainerWidth * offsetMultiplier;

    // Create text sprites for each paytable entry
    const textElements = [];

    displayEntries.forEach((entry, displayIndex) => {
      const lineY = startY + displayIndex * lineHeight;
      const symbolCountText = `${entry.symbolCount}:`;
      const payoutText = `${entry.formattedPayout} ${currency || ""}`.trim();

      // Create symbol count text
      const symbolCountSprite = new PIXI.Text(symbolCountText, {
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: adjustedFontSize,
        fontWeight: "bold",
        fill: entry.symbolColor,
        align: "left",
      });
      symbolCountSprite.x = Math.round(textStartX);
      symbolCountSprite.y = Math.round(lineY);
      symbolCountSprite.resolution = 2;
      symbolCountSprite.name = `paytable_symbol_count_${displayIndex}`;
      textElements.push(symbolCountSprite);

      // Calculate payout text position
      const symbolCountWidth = symbolCountSprite.width;
      const payoutX = Math.round(textStartX + symbolCountWidth + adjustedGap);

      // Create payout text
      const payoutSprite = new PIXI.Text(payoutText, {
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: adjustedFontSize,
        fontWeight: "bold",
        fill: entry.payoutColor,
        align: "left",
      });
      payoutSprite.x = payoutX;
      payoutSprite.y = Math.round(lineY);
      payoutSprite.resolution = 2;
      payoutSprite.name = `paytable_payout_${displayIndex}`;
      textElements.push(payoutSprite);
    });

    return textElements;
  }

  /**
   * Calculates scale factor to fit paytable text within container width
   * Scales down font size if text would overflow
   */
  _calculatePaytableScaleFactor(
    displayEntries,
    fontSize,
    availableWidth,
    currency
  ) {
    const baseStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: fontSize,
      fontWeight: "bold",
    });

    let minScaleFactor = 1;

    displayEntries.forEach((entry) => {
      const symbolCountText = `${entry.symbolCount}:`;
      const payoutText = `${entry.formattedPayout} ${currency || ""}`.trim();

      const symbolCountWidth = PIXI.TextMetrics.measureText(
        symbolCountText,
        baseStyle
      ).width;
      const payoutWidth = PIXI.TextMetrics.measureText(
        payoutText,
        baseStyle
      ).width;
      const gap = fontSize * INFO_SYMBOL_POPUP_CONFIG.TEXT_GAP_MULTIPLIER;
      const totalTextWidth = symbolCountWidth + gap + payoutWidth;

      if (totalTextWidth > availableWidth) {
        const scaleFactor = availableWidth / totalTextWidth;
        minScaleFactor = Math.min(minScaleFactor, scaleFactor);
      }
    });

    return minScaleFactor;
  }

  /**
   * Updates clickable area positions and sizes on resize
   * Maintains grid alignment with symbol positions
   */
  _updateClickableAreaPositions() {
    const { symbolWidth, symbolHeight, reelSpacing } = this.dimensions;

    let areaIndex = 0;
    for (let reelIndex = 0; reelIndex < 5; reelIndex++) {
      for (let symbolIndex = 0; symbolIndex < 4; symbolIndex++) {
        const clickArea = this.clickableAreas[areaIndex];
        if (clickArea) {
          clickArea.width = symbolWidth;
          clickArea.height = symbolHeight;
          clickArea.x = Math.round(reelSpacing * (reelIndex - 2));
          clickArea.y = Math.round(
            symbolHeight * symbolIndex +
            symbolHeight * INFO_SYMBOL_POPUP_CONFIG.SYMBOL_Y_OFFSET_MULTIPLIER
          );
        }
        areaIndex++;
      }
    }
  }

  /**
   * Handles resize - updates clickable areas and recreates active popup
   * Ensures popup maintains correct position and scale after dimension changes
   */
  resize(newDimensions) {
    this.dimensions = newDimensions;
    this.device = newDimensions.device;

    // Update background click area size
    if (this.backgroundClickArea) {
      this.backgroundClickArea.width = newDimensions.width * 3;
      this.backgroundClickArea.height = newDimensions.height * 3;
    }

    // Update clickable areas
    this._updateClickableAreaPositions();

    // Recreate active popup if exists
    if (this.activePopup) {
      const { reelIndex, symbolIndex } = this.activePopup;
      this.openPopup(reelIndex, symbolIndex);
    }
  }

  /**
   * Sets interactive state of clickable areas
   * Disables interaction during spin, autoplay, Hold & Win, or win animations
   */
  setInteractive(interactive) {
    this.clickableAreas.forEach((area) => {
      area.eventMode = interactive ? "static" : "none";
    });
  }

  /**
   * Checks if current device is desktop
   * @returns {boolean} True if desktop
   */
  isDesktop() {
    return this.device === PixiSettings.DEVICE.DESKTOP;
  }

  /**
   * Checks if popup is currently active
   * @returns {boolean} True if popup is open
   */
  hasActivePopup() {
    return !!this.activePopup;
  }

  /**
   * Updates bet value and refreshes popup if open
   * For coin symbols: Don't refresh (keep original values from completed spin)
   * For other symbols: Refresh with new bet-multiplied paytable values
   */
  updateBet(newBet) {
    this.bet = newBet || 1;

    // If popup is open, check if it's a coin symbol
    if (this.activePopup) {
      const { symbolId } = this.activePopup;

      // If it's a coin symbol, don't refresh
      // Coin values should remain frozen at their original values from the completed spin
      if (isCoinSymbol(symbolId)) {
        return; // Skip refresh for coins
      }

      // For non-coin symbols (paytable entries), refresh with new bet values
      const { reelIndex, symbolIndex } = this.activePopup;
      this.closePopup();
      this.openPopup(reelIndex, symbolIndex);
    }
  }

  /**
   * Updates base bet value and refreshes popup if open
   * Recreates popup with new paytable calculations (paytable_value * (bet / baseBet))
   */
  updateBaseBet(newBaseBet) {
    this.baseBet = newBaseBet || 1;

    // If popup is open, recreate it with new base bet values
    if (this.activePopup) {
      const { reelIndex, symbolIndex } = this.activePopup;
      this.closePopup();
      this.openPopup(reelIndex, symbolIndex);
    }
  }

  /**
   * Calculates display value for coin symbols (coinValue * bet) with locale formatting
   * @param {string} symbolValue - Coin value from SymbolData (e.g., "1", "2", "5")
   * @returns {string} Formatted display value (e.g., "1.00", "2.00") or empty string if invalid
   */
  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      const displayValue = coinValue * this.bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  /**
   * Cleanup - destroys all sprites and removes event listeners
   * Called when component unmounts
   */
  destroy() {
    this.closePopup();
    this.clickableAreas.forEach((area) => {
      area.removeAllListeners();
      area.destroy();
    });
    this.clickableAreas = [];

    if (this.backgroundClickArea) {
      this.backgroundClickArea.removeAllListeners();
      this.backgroundClickArea.destroy();
      this.backgroundClickArea = null;
    }
  }
}

export default InfoOverlayController;
